import { Component, OnInit, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { AuthService } from '../../../core/auth/auth.service';
import { LoginRequest, LoginResponse } from '../../../core/auth/auth.models';
import { TenantBrandingService } from '../../../core/branding/tenant-branding.service';
import { I18nService } from '../../../core/i18n/i18n.service';
import { AppLanguage } from '../../../core/i18n/i18n.models';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { LoginFormComponent } from '../components/login-form.component';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [
    TranslatePipe,
    LoginFormComponent,
    RouterLink,
  ],
  template: `
    <div class="login-page" [style.--tenant-primary]="tenantPrimaryColor" [style.--tenant-secondary]="tenantSecondaryColor" [style.--tenant-accent]="tenantAccentColor">
      <div class="login-page__card">
        <div class="login-page__header">
          <div>
            @if (brandingLogoUrl) {
              <img [src]="brandingLogoUrl" alt="Tenant logo" class="login-page__logo" />
            }
            <h1 class="login-page__title">{{ tenantDisplayName || ('app.brand' | t) }}</h1>
            <p class="login-page__subtitle">
              {{ 'auth.signInSubtitle' | t }}
            </p>
          </div>
          <div class="login-page__lang">
            <label for="login-lang">{{ 'app.language' | t }}</label>
            <select id="login-lang" [value]="language" (change)="onLanguageChange($any($event.target).value)">
              @for (option of languageOptions; track option.code) {
                <option [value]="option.code">{{ option.label }}</option>
              }
            </select>
          </div>
        </div>

        @if (tenantSlug) {
          <app-login-form
          [isSubmitting]="isSubmitting"
          [errorText]="errorText"
          [tenantSlug]="tenantSlug"
          [twoFactorRequired]="twoFactorRequired"
          (submitted)="onSubmitted($event)"
          />
        } @else {
          <div class="login-page__tenant-required">
            {{ 'auth.loginTenantUrlRequired' | t }}
          </div>
        }

        @if (tenantSlug && !twoFactorRequired) {
          <p class="login-page__register-link">
            <a [routerLink]="['/', tenantSlug, 'register']">{{ 'auth.createClientAccount' | t }}</a>
          </p>
        }
      </div>
    </div>
  `,
  styles: [`
    .login-page {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background:
        radial-gradient(circle at top left, color-mix(in srgb, var(--tenant-primary, #2563eb) 18%, transparent), transparent 34%),
        linear-gradient(160deg, color-mix(in srgb, var(--tenant-secondary, #0f172a) 7%, white), #f8fafc 55%);
      padding: 24px;
      box-sizing: border-box;
    }

    .login-page__card {
      width: 100%;
      max-width: 420px;
      background: #ffffff;
      border-radius: 16px;
      padding: 32px;
      box-sizing: border-box;
      box-shadow: 0 18px 40px color-mix(in srgb, var(--tenant-secondary, #0f172a) 16%, transparent);
    }

    .login-page__header {
      margin-bottom: 24px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 12px;
    }

    .login-page__logo {
      width: auto;
      max-width: 180px;
      max-height: 52px;
      display: block;
      margin-bottom: 10px;
      object-fit: contain;
    }

    .login-page__title {
      margin: 0 0 8px;
      font-size: 28px;
      color: var(--tenant-secondary, #111827);
    }

    .login-page__subtitle {
      margin: 0;
      color: #6b7280;
      font-size: 14px;
    }

    .login-page__lang {
      display: flex;
      flex-direction: column;
      gap: 6px;
      min-width: 90px;
    }

    .login-page__lang label {
      font-size: 12px;
      color: #374151;
      font-weight: 600;
    }

    .login-page__lang select {
      height: 34px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 8px;
      font-size: 13px;
      background: #fff;
    }

    .login-page__register-link {
      margin: 16px 0 0;
      font-size: 14px;
      color: #374151;
    }

    .login-page__tenant-required {
      padding: 12px 14px;
      border: 1px solid #fde68a;
      border-radius: 10px;
      background: #fffbeb;
      color: #92400e;
      font-size: 14px;
      line-height: 1.45;
    }
  `]
})
export class LoginPageComponent implements OnInit
{
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly i18n = inject(I18nService);
  private readonly tenantBranding = inject(TenantBrandingService);

  protected isSubmitting = false;
  protected errorText: string | null = null;
  protected twoFactorRequired = false;
  protected readonly tenantSlug = this.route.snapshot.paramMap.get('companySlug')?.trim().toLowerCase() ?? null;
  protected readonly languageOptions = this.i18n.languages;
  protected tenantDisplayName = '';
  protected brandingLogoUrl: string | null = null;
  protected tenantPrimaryColor = '#2563eb';
  protected tenantSecondaryColor = '#0f172a';
  protected tenantAccentColor = '#f59e0b';

  protected get language(): AppLanguage {
    return this.i18n.language;
  }

  public ngOnInit(): void
  {
    if (this.tenantSlug) {
      this.tenantBranding.getBranding(this.tenantSlug).subscribe((branding) => {
        const vars = this.tenantBranding.toCssVars(branding);
        this.tenantDisplayName = branding?.displayName ?? '';
        this.brandingLogoUrl = branding?.logoUrl ?? null;
        this.tenantPrimaryColor = vars['--tenant-primary'];
        this.tenantSecondaryColor = vars['--tenant-secondary'];
        this.tenantAccentColor = vars['--tenant-accent'];
      });
    }

    if (this.tenantSlug) {
      return;
    }

    const preferredSlug = this.authService.getPreferredCompanySlug();
    if (preferredSlug) {
      void this.router.navigate(['/', preferredSlug, 'login'], { replaceUrl: true });
    }
  }

  protected onLanguageChange(language: AppLanguage): void {
    this.i18n.setLanguage(language);
  }

  protected onSubmitted(payload: LoginRequest): void
  {
    if (!this.tenantSlug) {
      this.errorText = 'auth.loginTenantUrlRequired';
      return;
    }

    this.errorText = null;
    this.isSubmitting = true;

    const requestPayload: LoginRequest = {
      email: payload.email,
      password: payload.password,
      companySlug: this.tenantSlug,
      totpCode: payload.totpCode,
      trustedDeviceToken: payload.trustedDeviceToken,
      trustDevice: payload.trustDevice,
    };

    if (!requestPayload.totpCode) {
      requestPayload.trustedDeviceToken = this.authService.getTrustedDeviceToken() ?? undefined;
    }

    this.authService.login(requestPayload)
      .pipe(
        finalize(() =>
        {
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: (response: LoginResponse) =>
        {
          if ((response as { requiresTwoFactor?: boolean }).requiresTwoFactor) {
            this.twoFactorRequired = true;
            this.errorText = null;
            return;
          }

          this.twoFactorRequired = false;
          const targetSlug = this.tenantSlug;
          if ((response as { mustSetupTwoFactor?: boolean }).mustSetupTwoFactor) {
            void this.router.navigate(['/', targetSlug, 'security']);
            return;
          }

          void this.router.navigate(['/', targetSlug, 'appointments']);
        },
        error: (error: unknown) =>
        {
          if (error instanceof HttpErrorResponse && error.status === 403) {
            this.errorText = 'auth.loginEmailVerificationRequired';
            return;
          }

          this.errorText = 'auth.loginFailed';
        }
      });
  }
}
